import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border text-[0.95rem] font-semibold tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'border-primary/20 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground hover:-translate-y-0.5 hover:from-primary/95 hover:to-primary/80',
        destructive:
          'border-red-500/20 bg-gradient-to-r from-red-500 to-red-600 text-white hover:-translate-y-0.5 hover:from-red-500/95 hover:to-red-700',
        warning:
          'border-primary/25 bg-gradient-to-r from-primary/95 to-primary/80 text-primary-foreground hover:-translate-y-0.5 hover:from-primary hover:to-primary/85',
        outline:
          'border-white/12 bg-white/[0.04] text-foreground hover:-translate-y-0.5 hover:border-primary/25 hover:bg-white/[0.08] hover:text-foreground',
        secondary:
          'border-white/10 bg-white/[0.06] text-foreground hover:-translate-y-0.5 hover:border-white/15 hover:bg-white/[0.1]',
        ghost:
          'border-transparent bg-transparent text-foreground hover:bg-white/[0.08] hover:text-white',
        link: 'border-transparent bg-transparent text-primary underline-offset-4 hover:text-primary/80 hover:underline',
      },
      size: {
        default: 'h-11 px-5 py-2.5',
        sm: 'h-9 rounded-lg px-4 text-sm',
        lg: 'h-12 rounded-xl px-8',
        icon: 'h-10 w-10 rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
